import { z } from "zod";

/**
 * Shopping-list generator output (Phase 4 — "the artifact every client
 * sees"). Pure server math: guest_count x drinks_per_guest x menu share,
 * rolled up per ingredient, oz -> bottle/unit counts with a buffer. The
 * frontend NEVER recomputes any of this — every number here is rendered
 * as-is (oz rounded to 1 decimal for display only; see shoppingListFormat.ts).
 *
 * Contract note (ambiguity + resolution — see B4-2 handoff): the Phase 4
 * spec writes the `groups` shape literally only for `spirits`
 * (`{ingredient,total_oz,bottles_750ml}`) and elides mixers/garnish as
 * `[...]`. We resolve this by treating all three groups as the same line
 * shape (the ellipsis reuses the spirits shape) — `bottles_750ml` is
 * rendered under a per-group-appropriate column header ("Bottles",
 * "Units") but the value itself is never recomputed, only relabeled.
 */

export const ShoppingListLineSchema = z.object({
  ingredient: z.string(),
  total_oz: z.number(),
  bottles_750ml: z.number(),
});
export type ShoppingListLine = z.infer<typeof ShoppingListLineSchema>;

export const ShoppingListGroupsSchema = z.object({
  spirits: z.array(ShoppingListLineSchema),
  mixers: z.array(ShoppingListLineSchema),
  garnish: z.array(ShoppingListLineSchema),
});
export type ShoppingListGroups = z.infer<typeof ShoppingListGroupsSchema>;

export const ShoppingListPayloadSchema = z.object({
  alcohol_guests: z.number(),
  na_guests: z.number(),
  drinks_per_guest: z.number(),
  buffer_pct: z.number(),
  groups: ShoppingListGroupsSchema,
  line_count: z.number(),
});
export type ShoppingListPayload = z.infer<typeof ShoppingListPayloadSchema>;

export const ShoppingListSchema = z.object({
  event_id: z.string(),
  generated_at: z.string(),
  drinks_per_guest: z.number(),
  buffer_pct: z.number(),
  payload: ShoppingListPayloadSchema,
  pdf_key: z.string().nullable(),
});
export type ShoppingList = z.infer<typeof ShoppingListSchema>;

// ---- Mutation inputs ----------------------------------------------------

export interface GenerateShoppingListInput {
  eventId: string;
  drinks_per_guest?: number;
  buffer_pct?: number;
}

export interface GenerateShoppingListPdfResult {
  pdf_key?: string | null;
  url?: string;
}

export const GenerateShoppingListPdfResultSchema = z.object({
  pdf_key: z.string().nullable().optional(),
  url: z.string().optional(),
});
